import React, { useState } from 'react';
import {
  Building, Users, Palette, Bell, Shield, CreditCard,
  ChevronRight, Upload, Save, Check, Trash2, Edit2, Plus, X
} from 'lucide-react';

// Mock data
const tenantInfo = {
  name: 'FastShip Jamaica',
  slug: 'fastship',
  email: 'admin@fastshipja.com',
  phone: '+1 (876) 555-9999',
  address: '45 Knutsford Blvd, Kingston 5',
  plan: 'Professional',
  prefix: 'FS'
};

const brandingSettings = {
  primaryColor: '#8b5cf6',
  secondaryColor: '#06b6d4',
  logo: null
};

const teamMembers = [
  { id: 1, name: 'John Doe', email: 'john@fastshipja.com', role: 'owner', status: 'active' },
  { id: 2, name: 'Jane Smith', email: 'jane@fastshipja.com', role: 'admin', status: 'active' },
  { id: 3, name: 'Mike Johnson', email: 'mike@fastshipja.com', role: 'cashier', status: 'active' },
];

export default function TenantSettings() {
  const [activeTab, setActiveTab] = useState('company');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [company, setCompany] = useState(tenantInfo);
  const [branding, setBranding] = useState(brandingSettings);

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }, 1000);
  };

  const tabs = [
    { id: 'company', label: 'Company Info', icon: Building },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'team', label: 'Team', icon: Users },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  return (
    <div className="tenant-settings-page" data-testid="tenant-settings">
      <div className="settings-layout">
        {/* Settings Nav */}
        <div className="settings-nav">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`settings-nav-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={18} />
              <span>{tab.label}</span>
              <ChevronRight size={16} className="arrow" />
            </button>
          ))}
        </div>

        {/* Settings Content */}
        <div className="settings-content">
          {/* Company Info Tab */}
          {activeTab === 'company' && (
            <div className="settings-section" data-testid="company-section">
              <div className="section-header">
                <h2>Company Information</h2>
                <p>Manage your business details and contact information.</p>
              </div>

              <div className="settings-card">
                <h3>Business Details</h3>
                <div className="form-group">
                  <label>Company Name</label>
                  <input
                    type="text"
                    value={company.name}
                    onChange={(e) => setCompany({...company, name: e.target.value})}
                    data-testid="company-name"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Shipping Prefix</label>
                    <input
                      type="text"
                      value={company.prefix}
                      onChange={(e) => setCompany({...company, prefix: e.target.value})}
                      maxLength={3}
                      data-testid="company-prefix"
                    />
                    <p className="field-hint">Used in shipment IDs (e.g., FS-2025-001)</p>
                  </div>
                  <div className="form-group">
                    <label>URL Slug</label>
                    <input
                      type="text"
                      value={company.slug}
                      disabled
                      data-testid="company-slug"
                    />
                  </div>
                </div>
              </div>

              <div className="settings-card">
                <h3>Contact Information</h3>
                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={company.email}
                    onChange={(e) => setCompany({...company, email: e.target.value})}
                    data-testid="company-email"
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    value={company.phone}
                    onChange={(e) => setCompany({...company, phone: e.target.value})}
                    data-testid="company-phone"
                  />
                </div>
                <div className="form-group">
                  <label>Business Address</label>
                  <input
                    type="text"
                    value={company.address}
                    onChange={(e) => setCompany({...company, address: e.target.value})}
                    data-testid="company-address"
                  />
                </div>
              </div>

              <div className="settings-card plan-card">
                <h3>Current Plan</h3>
                <div className="plan-info">
                  <span className="plan-name">{company.plan}</span>
                  <span className="plan-features">Unlimited shipments, 10 team members, Priority support</span>
                </div>
                <button className="btn btn-secondary" data-testid="upgrade-plan">
                  Upgrade Plan
                </button>
              </div>
            </div>
          )}

          {/* Branding Tab */}
          {activeTab === 'branding' && (
            <div className="settings-section" data-testid="branding-section">
              <div className="section-header">
                <h2>Branding</h2>
                <p>Customize how your portal looks to your team and customers.</p>
              </div>

              <div className="settings-card">
                <h3>Logo</h3>
                <div className="logo-upload-section">
                  <div className="logo-preview tenant-logo-preview">
                    {branding.logo ? (
                      <img src={branding.logo} alt="Company Logo" />
                    ) : (
                      <div className="logo-placeholder" style={{background: branding.primaryColor}}>
                        {company.prefix}
                      </div>
                    )}
                  </div>
                  <div className="logo-upload-actions">
                    <button className="btn btn-secondary" data-testid="upload-logo">
                      <Upload size={14} /> Upload Logo
                    </button>
                    <p className="upload-hint">PNG or SVG, recommended 200x60px</p>
                  </div>
                </div>
              </div>

              <div className="settings-card">
                <h3>Colors</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Primary Color</label>
                    <div className="color-picker-group">
                      <input
                        type="color"
                        value={branding.primaryColor}
                        onChange={(e) => setBranding({...branding, primaryColor: e.target.value})}
                        className="color-picker"
                        data-testid="primary-color"
                      />
                      <input
                        type="text"
                        value={branding.primaryColor}
                        onChange={(e) => setBranding({...branding, primaryColor: e.target.value})}
                        className="color-text"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Secondary Color</label>
                    <div className="color-picker-group">
                      <input
                        type="color"
                        value={branding.secondaryColor}
                        onChange={(e) => setBranding({...branding, secondaryColor: e.target.value})}
                        className="color-picker"
                        data-testid="secondary-color"
                      />
                      <input
                        type="text"
                        value={branding.secondaryColor}
                        onChange={(e) => setBranding({...branding, secondaryColor: e.target.value})}
                        className="color-text"
                      />
                    </div>
                  </div>
                </div>
                <div className="color-preview-section">
                  <h4>Preview</h4>
                  <div className="color-preview-bar">
                    <div className="preview-block" style={{background: branding.primaryColor}}>Primary</div>
                    <div className="preview-block" style={{background: branding.secondaryColor}}>Secondary</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Team Tab */}
          {activeTab === 'team' && (
            <div className="settings-section" data-testid="team-section">
              <div className="section-header">
                <h2>Team Members</h2>
                <p>Manage who has access to your tenant portal.</p>
              </div>

              <div className="settings-card team-card">
                <div className="team-header">
                  <h3>Team ({teamMembers.length})</h3>
                  <button 
                    className="btn btn-primary"
                    onClick={() => { setEditingUser(null); setShowUserModal(true); }}
                    data-testid="invite-user"
                  >
                    <Plus size={14} /> Invite User
                  </button>
                </div>

                <div className="team-list">
                  {teamMembers.map((member) => (
                    <div key={member.id} className="team-member" data-testid={`member-${member.id}`}>
                      <div className="member-avatar">
                        {member.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div className="member-info">
                        <span className="member-name">{member.name}</span>
                        <span className="member-email">{member.email}</span>
                      </div>
                      <span className={`role-badge ${member.role}`}>{member.role}</span>
                      <div className="member-actions">
                        {member.role !== 'owner' && (
                          <>
                            <button 
                              className="action-btn"
                              onClick={() => { setEditingUser(member); setShowUserModal(true); }}
                            >
                              <Edit2 size={14} />
                            </button>
                            <button className="action-btn delete">
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="settings-section" data-testid="notifications-section">
              <div className="section-header">
                <h2>Notification Preferences</h2>
                <p>Configure how you receive updates about your account.</p>
              </div>

              <div className="settings-card">
                <h3>Email Notifications</h3>
                <div className="toggle-list">
                  <div className="toggle-item">
                    <div className="toggle-info">
                      <h4>New Shipments</h4>
                      <p>Get notified when new shipments are received</p>
                    </div>
                    <label className="toggle-switch">
                      <input type="checkbox" defaultChecked />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  <div className="toggle-item">
                    <div className="toggle-info">
                      <h4>Invoice Generated</h4>
                      <p>Receive emails when new invoices are created</p>
                    </div>
                    <label className="toggle-switch">
                      <input type="checkbox" defaultChecked />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  <div className="toggle-item">
                    <div className="toggle-info">
                      <h4>Payment Reminders</h4>
                      <p>Get reminded about upcoming or overdue payments</p>
                    </div>
                    <label className="toggle-switch">
                      <input type="checkbox" defaultChecked />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <div className="settings-section" data-testid="billing-section">
              <div className="section-header">
                <h2>Billing & Subscription</h2>
                <p>Manage your subscription and payment methods.</p>
              </div>

              <div className="settings-card">
                <h3>Current Plan</h3>
                <div className="plan-details">
                  <div className="plan-badge professional">Professional</div>
                  <p>JMD 25,000/month</p>
                  <ul className="plan-features-list">
                    <li>Unlimited shipments</li>
                    <li>Up to 10 team members</li>
                    <li>Priority support</li>
                    <li>Custom branding</li>
                  </ul>
                </div>
                <button className="btn btn-secondary" data-testid="manage-subscription">
                  Manage Subscription
                </button>
              </div>

              <div className="settings-card">
                <h3>Payment Method</h3>
                <div className="payment-method">
                  <div className="card-icon">VISA</div>
                  <div className="card-info">
                    <span>**** **** **** 4242</span>
                    <span className="card-expiry">Expires 12/26</span>
                  </div>
                </div>
                <button className="btn btn-secondary" data-testid="update-payment">
                  Update Payment Method
                </button>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="settings-section" data-testid="security-section">
              <div className="section-header">
                <h2>Security</h2>
                <p>Manage your account security settings.</p>
              </div>

              <div className="settings-card">
                <h3>Password</h3>
                <p className="card-description">Last changed 45 days ago</p>
                <button className="btn btn-secondary" data-testid="change-password">
                  Change Password
                </button>
              </div>

              <div className="settings-card">
                <h3>Two-Factor Authentication</h3>
                <div className="toggle-item single">
                  <div className="toggle-info">
                    <h4>Enable 2FA</h4>
                    <p>Add an extra layer of security to your account</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="settings-actions">
            <button 
              className={`btn btn-primary ${saved ? 'saved' : ''}`}
              onClick={handleSave}
              disabled={saving}
              data-testid="save-settings"
            >
              {saving ? 'Saving...' : saved ? (
                <><Check size={16} /> Saved!</>
              ) : (
                <><Save size={16} /> Save Changes</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingUser ? 'Edit User' : 'Invite User'}</h3>
              <button className="close-btn" onClick={() => setShowUserModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Full Name</label>
                <input type="text" defaultValue={editingUser?.name || ''} placeholder="Enter name" />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" defaultValue={editingUser?.email || ''} placeholder="email@company.com" />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select defaultValue={editingUser?.role || 'viewer'}>
                  <option value="admin">Admin</option>
                  <option value="cashier">Cashier</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowUserModal(false)}>Cancel</button>
              <button className="btn btn-primary">{editingUser ? 'Save' : 'Send Invite'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
